import { useLazyImage } from '../../../hooks/useLazyImage';

interface SoldVehicleCardProps {
  vehicle: {
    id: number;
    name: string;
    make: string;
    model: string;
    year: number;
    soldPrice: number;
    mileage: number;
    bodyType: string;
    fuelType: string;
    transmission: string;
    drivetrain: string;
    exteriorColor: string;
    soldDate: string;
    daysOnLot: number;
    daysOnMarket?: number;
    image: string;
    buyerCity: string;
  };
  onFindSimilar: (vehicle: { make: string; model: string; year: number; bodyType: string }) => void;
  priority?: boolean;
}

export default function SoldVehicleCard({ vehicle, onFindSimilar, priority = false }: SoldVehicleCardProps) {
  const { ref, isVisible } = useLazyImage({ rootMargin: '200px' });

  const soldDateFormatted = new Date(vehicle.soldDate).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const imageTitle = `${vehicle.name} — Sold for $${vehicle.soldPrice.toLocaleString()} at Rockhound Auto, Warrenton Oregon`;
  const imageAlt = `${vehicle.name} — ${vehicle.mileage.toLocaleString()} miles — Sold vehicle from Rockhound Auto used car inventory`;

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-[#E8E2D6]">
      {/* aspect-[4/3] = 4:3 ratio → explicit width=400 height=300 prevents CLS */}
      <div ref={priority ? undefined : ref} className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        {(priority || isVisible) ? (
          <picture>
            <source
              type="image/avif"
              srcSet={`${vehicle.image}&w=400&fm=avif 400w, ${vehicle.image}&w=600&fm=avif 600w`}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            />
            <source
              type="image/webp"
              srcSet={`${vehicle.image}&w=400&fm=webp 400w, ${vehicle.image}&w=600&fm=webp 600w`}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            />
            <img
              src={`${vehicle.image}&w=400`}
              srcSet={`${vehicle.image}&w=400 400w, ${vehicle.image}&w=600 600w`}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              alt={imageAlt}
              title={imageTitle}
              loading={priority ? 'eager' : 'lazy'}
              decoding={priority ? 'sync' : 'async'}
              fetchPriority={priority ? 'high' : 'low'}
              width={400}
              height={300}
              className="w-full h-full object-cover"
            />
          </picture>
        ) : (
          <div className="w-full h-full bg-gray-200 animate-pulse" aria-hidden="true" />
        )}
        <div className="absolute top-4 right-4 bg-[#1C2B1E] text-white px-4 py-2 rounded-full font-semibold">
          SOLD
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="font-['Oswald'] text-xl font-bold text-[#1C2B1E] mb-2">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h3>
        
        <div className="flex items-center gap-4 text-sm text-[#1C2B1E]/60 mb-4">
          <span className="flex items-center gap-1">
            <i className="ri-dashboard-line text-[#E8B84B]"></i>
            {vehicle.mileage.toLocaleString()} mi
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line text-[#E8B84B]"></i>
            {soldDateFormatted}
          </span>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-[#E8E2D6] mb-4">
          <div>
            <p className="text-sm text-[#1C2B1E]/60 mb-1">Sold Price</p>
            <p className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E]">
              ${vehicle.soldPrice.toLocaleString()}
            </p>
          </div>
          
          {vehicle.daysOnMarket && (
            <div className="text-right">
              <p className="text-sm text-[#1C2B1E]/60 mb-1">Days Listed</p>
              <p className="font-semibold text-[#1C2B1E]">
                {vehicle.daysOnMarket}
              </p>
            </div>
          )}
        </div>

        {/* Find Me One Like This Button */}
        <button
          onClick={() => onFindSimilar({
            make: vehicle.make,
            model: vehicle.model,
            year: vehicle.year,
            bodyType: vehicle.bodyType
          })}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-teal-600 text-teal-700 rounded-lg font-semibold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-search-eye-line text-lg"></i>
          Find Me One Like This
        </button>
      </div>
    </div>
  );
}